import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { toAsync } from '@client/new/helpers/reactQuery';
import type { Async } from '@client/new/helpers/types';

import {
  AutomationStatsResponse,
  BrowseAutomationLogsResponse,
  getAutomationLogs,
  getAutomationStats,
} from '../automations.api';
import { AutomationLogsFilter, automationKeys } from '../automations.keys';
import { AutomationsRequestErrorKind, errorKindOf } from './Automations.model';

export type AutomationInsightsError = {
  kind: AutomationsRequestErrorKind;
  retry: () => Promise<void>;
};

/**
 * Always requests `byVersion=true`: the versions breakdown is what lets the
 * ViewModel surface "previous version, draining" without a second fetch, and
 * the response stays valid when only one version exists.
 */
export const useAutomationStatsModel = (
  creatorId: number,
): Async<AutomationStatsResponse, AutomationInsightsError> => {
  const query = useQuery({
    queryKey: automationKeys.stats(creatorId),
    queryFn: () => getAutomationStats(creatorId, true),
    retry: false,
  });

  return toAsync(query, (error) => ({
    kind: errorKindOf(error),
    retry: async () => {
      await query.refetch();
    },
  }));
};

type AutomationLogsModel = BrowseAutomationLogsResponse & {
  isRefreshing: boolean;
};

/**
 * `keepPreviousData` keeps the current page on screen while the next one
 * loads, so paginating doesn't collapse the table into a spinner;
 * `isRefreshing` lets the View dim the stale rows instead.
 */
export const useAutomationLogsModel = (
  creatorId: number,
  filter: AutomationLogsFilter,
): Async<AutomationLogsModel, AutomationInsightsError> => {
  const query = useQuery({
    queryKey: automationKeys.logs(creatorId, filter),
    queryFn: () => getAutomationLogs(creatorId, filter),
    retry: false,
    placeholderData: keepPreviousData,
  });

  const data = toAsync(query, (error) => ({
    kind: errorKindOf(error),
    retry: async () => {
      await query.refetch();
    },
  }));

  if (data.type !== 'fetched') return data;
  return {
    type: 'fetched',
    value: { ...data.value, isRefreshing: query.isFetching },
  };
};
